import type { FileRejection } from "react-dropzone";

import React, { useCallback, useMemo } from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { FileUploadOverlay } from "@v2/features/assets/components/FileUploadOverlay";
import { ACCEPT_FILE_TYPES } from "@v2/features/assets/constants";
import { getAcceptTypesForAssetTypes } from "@v2/features/assets/utils";
import { useChatFileDropZoneContext } from "@v2/features/chat/hooks/useChatFileDropZone";
import { useDocumentDrop } from "@v2/features/chat/hooks/useDocumentDrop";
import { useCurrentChatContext } from "@v2/features/chat/providers/CurrentChatContext";
import { handleFileRejections } from "@v2/features/products/utils";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { useDropzone } from "react-dropzone";

type ChatFileDropZoneProps = React.PropsWithChildren<{
  className?: string;
  style?: React.CSSProperties;
}>;

function ChatFileDropZone({
  children,
  className,
  style,
}: ChatFileDropZoneProps) {
  const { addFiles } = useCurrentChatContext();
  const { interceptor } = useChatFileDropZoneContext();
  const errorToast = useErrorToast();

  // NOTE (Kevin, 2025-12-15): When an interceptor is active, use its accepted types
  // to build the accept filter. This ensures only valid files for the interceptor are accepted.
  const acceptTypes = useMemo(() => {
    if (interceptor) {
      return getAcceptTypesForAssetTypes(interceptor.acceptedTypes);
    }
    return ACCEPT_FILE_TYPES;
  }, [interceptor]);

  const handleFiles = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      // NOTE (Kevin, 2025-12-15): If an interceptor is active, route files to it instead of chat
      if (interceptor) {
        interceptor.handler(acceptedFiles);
        return;
      }

      void addFiles(acceptedFiles);
    },
    [interceptor, addFiles],
  );

  // NOTE (Kevin, 2025-12-15): For the document drop, we need to filter files manually since
  // the document-level listener doesn't have built-in accept filtering like react-dropzone.
  const acceptedFileFilter = useCallback(
    (file: File) => {
      const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      const mimeType = file.type;
      return acceptTypes.includes(extension) || acceptTypes.includes(mimeType);
    },
    [acceptTypes],
  );

  const handleDocumentRejections = useCallback(
    (rejectedFiles: File[]) => {
      if (rejectedFiles.length === 0) {
        return;
      }
      // NOTE (Kevin, 2025-12-15): Convert rejected files to FileRejection format
      // so we can reuse the same error handling logic
      const fileRejections: FileRejection[] = rejectedFiles.map((file) => ({
        file,
        errors: [
          { code: "file-invalid-type", message: "File type not accepted" },
        ],
      }));
      handleFileRejections(
        fileRejections,
        errorToast,
        interceptor?.acceptedTypes,
      );
    },
    [errorToast, interceptor?.acceptedTypes],
  );

  const { isDragging: isDocumentDragging } = useDocumentDrop({
    onDrop: handleFiles,
    onRejected: handleDocumentRejections,
    acceptedFileFilter,
  });

  // NOTE (Kevin, 2025-12-17): useDocumentDrop handles all drag-and-drop because it can
  // detect drags over the Daytona iframe (via postMessage), which react-dropzone cannot
  // due to cross-origin restrictions. react-dropzone is only used here for the hidden
  // file input (programmatic file picker).
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptTypes,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
  });

  return (
    <div
      {...getRootProps({
        className: twMerge("h-full w-full cursor-default relative", className),
        style,
      })}
    >
      <input {...getInputProps()} />
      <FileUploadOverlay
        isDragging={isDocumentDragging}
        acceptedTypes={interceptor?.acceptedTypes ?? null}
      />
      {children}
    </div>
  );
}

export default ChatFileDropZone;
